import {
    Component,
    ViewChild,
    ViewEncapsulation,
    OnInit,
    ElementRef,
} from '@angular/core';
import {
    FormGroup,
    FormBuilder,
    FormArray,
    Validators,
    FormControl,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../lib/api.service';
import { LogService } from '../../../lib/log.service';
import { ModalService } from '../../../lib/modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Course, Chapter, Video, Attachment } from '../../../models/course';
import { Organization } from 'app/models/organization';
import { MultiAutoItem } from 'app/models/multiautoitem';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
@Component({
    selector: 'app-course-admin-edit',
    templateUrl: './course-admin-edit.component.html',
    styleUrls: ['./course-admin-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class CourseAdminEditComponent implements OnInit {
    @ViewChild('input') fileInput: ElementRef;
    courseEditForm: FormGroup;
    courseListingId: number;
    courseKey: string;
    title: string;
    mode: string;
    course: Course;
    isLoaded: boolean = false;
    config: any;
    panelState: Array<boolean>;
    submitButtonPressed: boolean = false;
    errors: string = '';
    categoryList: any = [];
    showFrench = false;
    showSpanish = false;
    seriesPageType: string;
    organizations: Organization[];
    multiSelOrgs: Array<MultiAutoItem> = [];
    public Editor = ClassicEditor;

    constructor(
        private translate: TranslateService,
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location
    ) {
        const paramId = this.route.snapshot.paramMap.get('id');
        this.courseListingId = paramId ? +paramId : null;
        this.seriesPageType =
            this.route.snapshot.queryParamMap.get('seriesPageType') || '';
        this.panelState = [];

        if (this.courseListingId) {
            this.title = 'Edit this Series';
            this.mode = 'update';
        } else {
            this.title = 'Create a Series';
            this.course = new Course({});
            this.mode = 'create';
            this.isLoaded = true;
        }
        this.getOrganizations();
        this.getCategoryList();

        this.createCourseEditForm(new Course({}));

        this.config = {
            toolbar: [
                'bold',
                'italic',
                'strikethrough',
                '|',
                'numberedList',
                'bulletedList',
                '|',
                'link',
                'pasteFromOffice',
            ],
        };
    }

    getCategoryList() {
        this.api.get('series/categories').subscribe(
            (result: any) => {
                this.categoryList = result;
            },
            (error: any) => {
                this.log.error('Error getting organizations. ' + error.message);
            }
        );
    }

    getOrganizations() {
        this.api.get('admin/organizations', { Active: true }).subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
                let orgList = [];
                this.organizations.forEach(org => {
                    orgList.push(new MultiAutoItem(org.id, org.name));
                });
                this.multiSelOrgs = [...orgList];
            },
            (error: any) => {
                this.log.error('Error loading. ' + error.message);
            }
        );
    }

    onSelectOrgs(items: MultiAutoItem[]) {
        let organizationIds = items.map(item => item.id);
        this.courseEditForm.patchValue({
            orgIds: organizationIds,
        });
    }

    get f() {
        return this.courseEditForm.controls;
    }

    createCourseEditForm(data: any) {
        this.courseEditForm = this.formBuilder.group({
            id: [data.id || null],
            label: [data.label || '', [Validators.required]],
            label_fr: [data.label_fr || ''],
            label_es: [data.label_es || ''],
            key: [data.key || ''],
            description: [data.description || ''],
            description_fr: [data.description_fr || ''],
            description_es: [data.description_es || ''],
            length: [data.length || ''],
            isCompleted: [data.isCompleted || false],
            orgIds: [data.orgIds || ''],
            talkAboutDescription: [data.talkAboutDescription || ''],
            talkAboutDescriptionfr: [data.talkAboutDescriptionfr || ''],
            talkAboutDescriptiones: [data.talkAboutDescriptiones || ''],
            default: [data.default || false],
            active: [data.active || false],
            chapters: this.formBuilder.array([]),
            courseImage: this.createFileUpload(data.courseImage),
            imageUrl: [data.imageUrl || ''],
        });
        if (this.seriesPageType !== 'customType') {
            this.courseEditForm.addControl(
                'category',
                this.formBuilder.control(data.category || '', [
                    Validators.required,
                ])
            );
            this.courseEditForm.addControl(
                'default',
                this.formBuilder.control(data.default || false)
            );
        } else {
            this.courseEditForm.addControl(
                'orgIds',
                this.formBuilder.control(data.orgIds || '')
            );
        }

        data.chapters?.forEach((chapter: any) => {
            this.addCourseItem(chapter);
        });
    }

    get chapters() {
        return this.courseEditForm.get('chapters') as FormArray;
    }

    getChapterFormGroup(index: number): FormGroup {
        const chaptersForm = this.courseEditForm.get('chapters') as FormArray;
        const formGroup = chaptersForm.controls[index] as FormGroup;
        return formGroup;
    }

    getVideosList(i: number) {
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        return videosList;
    }

    getVideoFormGroup(i: number, r: number): FormGroup {
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        const formGroup = videosList.controls[r] as FormGroup;
        return formGroup;
    }

    getAttachmentList(i: number, r: number) {
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        const attachmentList = videosList.at(r).get('attachments') as FormArray;
        return attachmentList;
    }

    ngOnInit() {
        if (this.courseListingId) {
            let payLoad = {};
            if (this.seriesPageType == 'customType') {
                payLoad['CourseType'] = 'organization';
            }
            this.api
                .get('series/serieslisting/' + this.courseListingId, payLoad)
                .subscribe(
                    (result: any) => {
                        const key = Object.keys(result.data)[0];
                        this.course = new Course(result.data[key], true);

                        // this.organization = new Organization(result.data, 'full');
                        this.createCourseEditForm(this.course);
                        this.isLoaded = true;
                    },
                    (error: any) => {
                        this.log.error(
                            'Error getting organizations. ' + error.message
                        );
                    }
                );
        }
    }

    changeVideoUploadListener($event, i: number, j: number): void {
        this.readFile(
            $event.target,
            i,
            j,
            null,
            'mediaUpload',
            'mpeg|mpg|mp4|mkv'
        );
    }

    changeVideoImageUploadListener($event, i: number, j: number): void {
        this.readFile(
            $event.target,
            i,
            j,
            null,
            'imageUpload',
            'gif|jpg|jpeg|png'
        );
    }

    changeCaptionUploadListener($event, i: number, j: number): void {
        this.readFile($event.target, i, j, null, 'captionFileUpload', 'vtt');
    }

    changeAudioUploadListener($event, i: number, j: number): void {
        this.readFile($event.target, i, j, null, 'audioFileUpload', 'mp3');
    }

    changeAttachmentUploadListener(
        $event,
        i: number,
        j: number,
        r: number
    ): void {
        this.readFile($event.target, i, j, r, 'attachments', 'pdf');
    }

    readFile(
        inputValue: any,
        i: number,
        j: number,
        r: number,
        keyFileName: string,
        allowedTypes: string = 'gif|jpg|jpeg|png|mpeg|mpg|mp4|mkv|vtt|mp3|pdf'
    ): void {
        var file: File = inputValue.files[0];
        inputValue.files[0];
        let fileName = inputValue.files[0].name;
        var reader: FileReader = new FileReader();
        const extPattern = new RegExp(`\.(${allowedTypes})$`, 'i');

        if (extPattern.test(fileName)) {
            reader.onloadend = e => {
                if (r !== null) {
                    // this.course.chapters[i].videos[j][`${keyFileName}`][r]['fileUpload'] = reader.result;
                    // this.course.chapters[i].videos[j][`${keyFileName}`][r]['fileFilename'] = fileName;

                    const chapterList = this.courseEditForm.get(
                        'chapters'
                    ) as FormArray;
                    const videosList = chapterList
                        .at(i)
                        .get('videos') as FormArray;
                    const attachmentList = videosList
                        .at(j)
                        .get(`${keyFileName}`) as FormArray;
                    const uploadItem = attachmentList.at(r) as FormGroup;

                    uploadItem.patchValue({
                        attachment: reader.result,
                        filePath: fileName,
                    });
                } else {
                    const chapterList = this.courseEditForm.get(
                        'chapters'
                    ) as FormArray;
                    const videosList = chapterList
                        .at(i)
                        .get('videos') as FormArray;
                    const uploadItem = videosList
                        .at(j)
                        .get(`${keyFileName}`) as FormGroup;

                    uploadItem.patchValue({
                        fileUpload: reader.result,
                        fileFilename: fileName,
                    });
                    // this.course.chapters[i].videos[j][`${keyFileName}`]['fileUpload'] = reader.result;
                    // this.course.chapters[i].videos[j][`${keyFileName}`]['fileFilename'] = fileName;
                }
            };
        } else {
            this.modalService.showAlert(
                'Error',
                `The extension is invalid. Valid extension(s) are: ${allowedTypes}`
            );
            inputValue.value = '';
        }

        reader.readAsDataURL(file);
    }

    doSave() {
        this.course = new Course(this.courseEditForm.value, true);

        this.submitButtonPressed = true;

        if (!this.courseEditForm.valid) {
            this.courseEditForm.markAllAsTouched();
            this.scrollToFirstError();
            return;
        }

        this.errors = '';

        if (!this.course.chapters || this.course.chapters.length === 0) {
            this.errors = 'The series needs at least one chapter';
            return;
        } else {
            let j = 1;
            this.course.chapters.forEach(chapter => {
                // if (!chapter.name) {
                // 	errors += `Chapter name ${j} missing<br>`;
                // 	return;
                // }

                if (!chapter.videos || chapter.videos.length === 0) {
                    this.errors = 'The chapter needs at least one video lesson';
                    return;
                } else {
                    var i = 1;
                    chapter.videos.forEach(video => {
                        if (!video.label) {
                            this.errors = `Video title ${i} missing`;
                            return;
                        }

                        if (!video.label && !video.audioFile) {
                            this.errors += `Either a video file or an audio file for entry ${i} is mandatory`;
                            return;
                        }
                        i++;
                    });
                    j++;
                }
            });
        }

        if (this.errors) {
            // errors = "The following errors were found:<br>" + errors;
            // this.modalService.showAlert("Error", errors);
            return;
        }
        this.log.debug('series ouput');
        this.log.debug(Course.forAPI(this.course));
        this.isLoaded = false;
        if (this.mode === 'update') {
            this.api
                .put(
                    'series/serieslisting/' + this.courseListingId,
                    Course.forAPI(this.course, this.seriesPageType),
                    true
                )
                .subscribe(
                    (data: any) => {
                        this.modalService.showAlert(
                            'Success',
                            'Series has been updated'
                        );
                        this.isLoaded = true;
                        this.navigateToListingPage();
                    },
                    (error: any) => {
                        this.isLoaded = true;
                        this.modalService.showAlert(
                            'Error',
                            'Something went wrong. ' + error.message
                        );
                    }
                );
        } else {
            this.api
                .post(
                    'series/serieslisting',
                    Course.forAPI(this.course, this.seriesPageType),
                    true
                )
                .subscribe(
                    (data: any) => {
                        this.modalService.showAlert(
                            'Success',
                            'Series has been created'
                        );
                        this.isLoaded = true;
                        this.navigateToListingPage();
                    },
                    (error: any) => {
                        this.isLoaded = true;
                        this.modalService.showAlert(
                            'Error',
                            'Something went wrong. ' + error.message
                        );
                    }
                );
        }
    }

    addCourseItem(data: any) {
        this.course.chapters.push(new Chapter({}));
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        chapterList.push(this.createChapterList(data));

        if (data.videos) {
            data.videos.forEach((video: any) => {
                this.addVideo(chapterList.length - 1, video);
            });
        }
    }

    createChapterList(data: any): FormGroup {
        return this.formBuilder.group({
            id: [data.id || null],
            name: [data.name || '', [Validators.required]],
            namefr: [data.namefr || ''],
            namees: [data.namees || ''],
            description: [data.description || ''],
            description_fr: [data.description_fr || ''],
            description_es: [data.description_es || ''],
            label: [data.label || ''],
            label_fr: [data.label_fr || ''],
            label_es: [data.label_es || ''],
            length: [data.length || ''],
            order: [data.order || 0],
            isActive: [data.isActive || 0],
            code: [data.code || ''],
            videos: this.formBuilder.array([]),
        });
    }

    onRemoveCourseItem(i: number, data: any) {
        this.course.chapters.splice(i, 1);
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        chapterList.removeAt(i);
    }

    addVideo(i: number, data: any) {
        // this.course.chapters[i].videos.push(new Video({}));

        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        videosList.push(this.createVideoList(data));
    }

    createVideoList(data: any) {
        const videoList = this.formBuilder.group({
            id: [data.id || null],
            label: [data.label || '', [Validators.required]],
            label_fr: [data.label_fr || ''],
            label_es: [data.label_es || ''],
            length: [data.length || ''],
            image: [data.image || ''],
            media: [data.media || ''],
            captionFile: [data.captionFile || ''],
            audioFile: [data.audioFile || ''],
            forceVideo: [data.forceVideo || ''],
            description: [data.description || ''],
            description_fr: [data.description_fr || ''],
            description_es: [data.description_es || ''],
            isCompleted: [data.isCompleted || false],
            attachments: this.formBuilder.array([]),
            imageUpload: this.createFileUpload({}),
            mediaUpload: this.createFileUpload({}),
            captionFileUpload: this.createFileUpload({}),
            audioFileUpload: this.createFileUpload({}),
            inAppLinks: this.formBuilder.array(
                (data.inAppLinks ?? data.InAppLinks ?? []).map((l: any) =>
                    this.createInAppLinkGroup(l)
                )
            ),
        });

        const attachmentList = videoList.controls.attachments as FormArray;

        if (data && data.attachments) {
            data.attachments.forEach((attachment: any) => {
                attachmentList.push(this.createAttachment(attachment));
            });
        }
        return videoList;
    }

    createAttachment(data: any) {
        return this.formBuilder.group({
            id: [data.id || null],
            title: [data.title || ''],
            attachment: [data.attachment || ''],
            fileUpload: [data.fileUpload || ''],
            fileFilename: [data.fileFilename || ''],
            filePath: [data.filePath || ''],
            fileFullPath: [data.fileFullPath || ''],
        });
    }

    /**
     * After a failed save, scroll the first invalid field into view so
     * the admin can see what's wrong instead of staring at a "Please
     * check the errors" banner with no indication where they are.
     */
    private scrollToFirstError(): void {
        setTimeout(() => {
            const firstError = document.querySelector(
                '.error-message:not([hidden]), .ng-invalid.ng-touched'
            ) as HTMLElement | null;
            if (firstError && typeof firstError.scrollIntoView === 'function') {
                firstError.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }, 0);
    }

    removeVideo(i: number, j: number, event?: Event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        videosList.removeAt(j);
        if (this.course.chapters?.[i]?.videos) {
            this.course.chapters[i].videos.splice(j, 1);
        }
    }

    addVideoAttachment(i: number, j: number, data: any) {
        // this.course.chapters[i].videos[j].attachments.push(new Attachment({}));

        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        const attachmentList = videosList.at(j).get('attachments') as FormArray;
        attachmentList.push(this.createAttachment(data));
    }

    /** FormGroup shape for one in-app link row. */
    createInAppLinkGroup(data: any = {}) {
        return this.formBuilder.group({
            id: [data?.id ?? null],
            type: [data?.type ?? '', [Validators.required]],
            target: [data?.target ?? ''],
            label: [data?.label ?? '', [Validators.required]],
        });
    }

    /** Accessor used by the template to iterate over a video's links. */
    getInAppLinksList(i: number, j: number): FormArray {
        const chapterList = this.courseEditForm.get('chapters') as FormArray;
        const videosList = chapterList.at(i).get('videos') as FormArray;
        return videosList.at(j).get('inAppLinks') as FormArray;
    }

    addInAppLink(i: number, j: number) {
        this.getInAppLinksList(i, j).push(this.createInAppLinkGroup());
    }

    removeInAppLink(i: number, j: number, k: number) {
        this.getInAppLinksList(i, j).removeAt(k);
    }

    createFileUpload(data: any) {
        return this.formBuilder.group({
            fileUpload: [data.fileUpload || null],
            fileFilename: [data.fileFilename || ''],
        });
    }

    onClose() {
        this.location.back();
    }

    courseImageUpload($event) {
        var file: File = $event.target.files[0];
        if (!file) return;
        let fileName = file.name;
        var reader: FileReader = new FileReader();
        const maxFileSize = 5 * 1024 * 1024;
        const courseImage = this.courseEditForm.get('courseImage') as FormGroup;

        if (!/\.(jpg|jpeg|png|webp)$/i.test(fileName)) {
            this.fileInput.nativeElement.value = '';
            this.modalService.showAlert(
                'Error',
                'Please ensure that the file you are uploading is in PNG, JPG, JPEG, or WebP format.'
            );
            return;
        }

        if (file.size > maxFileSize) {
            this.fileInput.nativeElement.value = '';
            this.modalService.showAlert(
                'Error',
                'The file size exceeds the maximum limit of 5MB.'
            );
            return;
        }

        reader.onloadend = () => {
            if (reader.result) {
                courseImage.patchValue({
                    fileUpload: reader.result,
                    fileFilename: fileName,
                });
            }
        };

        reader.readAsDataURL(file);
    }

    navigateToListingPage() {
        if (this.seriesPageType == 'customType') {
            this.router.navigate(['/admin/custom-series-listing']);
        } else {
            this.router.navigate(['/admin/series-listing']);
        }
    }
}
